import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, Grid, CircularProgress, Skeleton } from '@mui/material';
import { useDispatch } from 'react-redux';
import { GetConversionRates } from '../../redux/actions/conversionRates';
import './styles.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

const brainFeatures = [
  'Zero-Hallucination Deterministic Math',
  'Pre-Transmission Data Intercept & Triage',
  'CFR 49 & 19 CFR Regulatory Mapping',
];

const muscleFeatures = [
  'Conditional Fund Allocations',
  'Multi-Party Synchronized Disbursement',
  'Cryptographic Audit Dossier Generation',
];

const currencies = [
  { code: 'EUR', name: 'Euro' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'ILS', name: 'Israeli Shekel' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'PLN', name: 'Polish Złoty' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'AED', name: 'UAE Dirham' },
];

const CheckIcon = ({ color = 'sky' }) => (
  <svg className={`pa-feature-list__check pa-feature-list__check--${color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const PlatformArchitecturePage = () => {
  const dispatch = useDispatch();

  // ── Currency converter state ───────────────────────────────────────────────
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const [usdAmount, setUsdAmount] = useState('');
  const [convertedAmount, setConvertedAmount] = useState('');
  const [initialRate, setInitialRate] = useState(0);
  const [fixedSide, setFixedSide] = useState('sell');
  const [conversionError, setConversionError] = useState('');
  const [rateFetching, setRateFetching] = useState(false);
  const [initialRateFetching, setInitialRateFetching] = useState(true);
  const [currencyOpen, setCurrencyOpen] = useState(false);

  const selectWrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectWrapperRef.current && !selectWrapperRef.current.contains(e.target)) {
        setCurrencyOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ── Currency conversion helpers ───────────────────────────────────────────
  const formatNumber = (value) => {
    const clean = value.replace(/[^\d.]/g, '');
    const parts = clean.split('.');
    if (parts.length > 2) return parts[0] + '.' + parts.slice(1).join('');
    if (clean.includes('.')) {
      const [integer, decimal] = clean.split('.');
      return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + decimal.slice(0, 2);
    }
    return clean.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleGetConversionRates = (currency, base_currency, amount) => {
    if (amount && amount.length > 0) {
      let query = '';
      query += `&buy_currency=${currency}`;
      query += `&sell_currency=${base_currency}`;
      query += `&fixed_side=${fixedSide}`;
      query += `&amount=${amount.replace(/[^0-9.]/g, '')}`;
      query += `&conversion_date_preference=earliest`;
      query += `&product=pay`;
      dispatch(GetConversionRates(query, setRateFetching)).then(({ res, statusCode }) => {
        if (statusCode === 200) {
          if (fixedSide === 'sell') {
            setConvertedAmount(formatNumber(res.data.clientBuyAmount + ''));
            setInitialRate(
              parseFloat(res.data.clientBuyAmount / usdAmount.replace(/[^0-9.]/g, '')).toFixed(4)
            );
          } else {
            setUsdAmount(formatNumber(res.data.clientSellAmount + ''));
            setInitialRate(
              parseFloat(convertedAmount.replace(/[^0-9.]/g, '') / res.data.clientSellAmount).toFixed(4)
            );
          }
          setConversionError('');
        } else {
          setConversionError(
            'The FOREX market is closed for the weekend/holiday. Please check back on the next business day when trading reopens.'
          );
          setConvertedAmount('');
          setUsdAmount('');
          setInitialRate(0);
        }
      });
    } else {
      setRateFetching(false);
    }
  };

  const handleGetInitialRates = (currency, base_currency) => {
    let query = '';
    query += `&buy_currency=${currency}`;
    query += `&sell_currency=${base_currency}`;
    query += `&fixed_side=sell`;
    query += `&amount=1`;
    query += `&conversion_date_preference=earliest`;
    query += `&product=pay`;
    dispatch(GetConversionRates(query, setInitialRateFetching)).then(({ res, statusCode }) => {
      if (statusCode === 200) {
        setInitialRate(parseFloat(res.data.clientBuyAmount).toFixed(4));
        setConversionError('');
      } else {
        setConversionError(
          'The FOREX market is closed for the weekend/holiday. Please check back on the next business day when trading reopens.'
        );
      }
    });
  };

  const handleConversion = (e) => {
    e.preventDefault();
    handleGetConversionRates(
      selectedCurrency,
      'USD',
      fixedSide === 'sell' ? usdAmount : convertedAmount
    );
  };

  useEffect(() => {
    handleGetInitialRates(selectedCurrency, 'USD');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency]);

  return (
    <Box className="pa-page">
      {/* Header */}
      <Box className="pa-header">
        <Container maxWidth="md">
          <Typography variant="h1" className="pa-header__title">
            Core Platform Architecture
          </Typography>
          <Typography variant="body1" className="pa-header__subtitle">
            LusidPay operates on a unified, two-engine infrastructure: an AI-driven compliance brain
            and a programmable settlement muscle. Designed to execute zero-hallucination workflows.
          </Typography>
        </Container>
      </Box>

      {/* Main panels */}
      <Container maxWidth="lg" className="pa-panels">

        {/* Panel 1 — Compliance Brain */}
        <Box className="pa-panel">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box className="pa-panel__icon-box pa-panel__icon-box--sky">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </Box>
              <Typography className="pa-panel__tag pa-panel__tag--sky">Semantic Extraction Engine</Typography>
              <Typography variant="h2" className="pa-panel__title">The Compliance "Brain"</Typography>
              <Typography className="pa-panel__body">
                Our proprietary OS ingests massive amounts of unstructured data—from 500-page commercial
                invoices to complex municipal certification documents.
              </Typography>
              <ul className="pa-feature-list">
                {brainFeatures.map((f) => (
                  <li key={f} className="pa-feature-list__item">
                    <CheckIcon color="sky" />
                    {f}
                  </li>
                ))}
              </ul>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className="pa-terminal">
                <div className="pa-terminal__line pa-terminal__line--muted">{'// Ingesting Payload: UNSTRUCTURED_BOM_992.pdf'}</div>
                <div className="pa-terminal__line pa-terminal__line--sky">&gt; Semantic Extraction: Active</div>
                <div className="pa-terminal__line">&gt; Validating 14,000 line items...</div>
                <div className="pa-terminal__line pa-terminal__line--emerald">&gt; 95% Lines Auto-Cleared (Variance &lt; 0.01%)</div>
                <div className="pa-terminal__line pa-terminal__line--amber">&gt; Anomaly Detected: Chapter 88 Assist flag.</div>
                <div className="pa-terminal__line pa-terminal__line--muted pa-terminal__line--mt">&gt; Routing to Compliance Manager Triage.</div>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Panel 2 — Settlement Muscle */}
        <Box className="pa-panel">
          <Grid container spacing={6} alignItems="center" direction={{ xs: 'column', md: 'row-reverse' }}>
            <Grid item xs={12} md={6}>
              <Box className="pa-panel__icon-box pa-panel__icon-box--emerald">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </Box>
              <Typography className="pa-panel__tag pa-panel__tag--emerald">Programmable Ledger</Typography>
              <Typography variant="h2" className="pa-panel__title">The Settlement "Muscle"</Typography>
              <Typography className="pa-panel__body">
                A financial ledger built on conditional allocations. Funds are staged securely and
                disbursements are fully synchronized with the successful execution of the Compliance Brain.
              </Typography>
              <ul className="pa-feature-list">
                {muscleFeatures.map((f) => (
                  <li key={f} className="pa-feature-list__item">
                    <CheckIcon color="emerald" />
                    {f}
                  </li>
                ))}
              </ul>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className="pa-terminal">
                <div className="pa-terminal__line pa-terminal__line--muted">{'// Ledger Instruction Set: DDP_SETTLEMENT'}</div>
                <div className="pa-terminal__line pa-terminal__line--emerald">&gt; Capital Staged: Awaiting Compliance Clearance</div>
                <div className="pa-terminal__line pa-terminal__line--sky pa-terminal__line--mb">&gt; SIGNAL: Brain Clearance Authorized.</div>
                <div className="pa-terminal__line pa-terminal__line--muted">Executing Synchronized Settlement:</div>
                <div className="pa-terminal__line">&nbsp;&nbsp;├─ Wire -&gt; Forwarder (Origin Freight)</div>
                <div className="pa-terminal__line">&nbsp;&nbsp;├─ Wire -&gt; CBP (Verified Duties)</div>
                <div className="pa-terminal__line">&nbsp;&nbsp;└─ Wire -&gt; Factory (Final Invoice)</div>
                <div className="pa-terminal__line pa-terminal__line--emerald pa-terminal__line--mt">&gt; STATUS: Cryptographic Ledger Updated.</div>
              </Box>
            </Grid>
          </Grid>
        </Box>

      </Container>

      {/* ── Engine 02: Treasury Execution ─────────────────── */}
      <section className="tsx-section" id="international-payments">
        <Container maxWidth="lg">

          {/* Section header */}
          <Box className="tsx-header">
            <Typography className="tsx-header__tag">Engine 02: Treasury Execution</Typography>
            <Typography variant="h4" className="tsx-header__title">
              Multi-Currency Settlement Execution
            </Typography>
            <Typography className="tsx-header__subtitle">
              Programmatic FX routing across 35+ currencies, mathematically synchronized with 19 CFR
              trade compliance clearance to eliminate slippage.
            </Typography>
          </Box>

          <Grid container spacing={6} alignItems="flex-start">

            {/* Left — numbered steps */}
            <Grid item xs={12} md={6}>
              <Box className="tsx-steps">

                <Box className="tsx-step">
                  <Box className="tsx-step__number tsx-step__number--slate">1</Box>
                  <Box>
                    <Typography className="tsx-step__title">Ingest Commercial Invoice</Typography>
                    <Typography className="tsx-step__desc">
                      Semantic extraction parses the unstructured commercial invoice, instantly
                      cross-referencing supplier details and valuation against the draft CBP Form 7501.
                    </Typography>
                  </Box>
                </Box>

                <Box className="tsx-step">
                  <Box className="tsx-step__number tsx-step__number--sky">2</Box>
                  <Box>
                    <Typography className="tsx-step__title">Programmatic FX Allocation</Typography>
                    <Typography className="tsx-step__desc">
                      Access live wholesale liquidity pools. Lock in competitive, institutional-grade
                      exchange rates and stage capital within the conditional ledger prior to clearance.
                    </Typography>
                  </Box>
                </Box>

                <Box className="tsx-step">
                  <Box className="tsx-step__number tsx-step__number--emerald">3</Box>
                  <Box>
                    <Typography className="tsx-step__title">Synchronized MT103 Disbursement</Typography>
                    <Typography className="tsx-step__desc">
                      Zero manual wire triggers. Automated, multi-node disbursement executes globally
                      the exact millisecond the AI compliance brain signals regulatory clearance.
                    </Typography>
                  </Box>
                </Box>

              </Box>
            </Grid>

            {/* Right — Treasury Routing Engine card */}
            <Grid item xs={12} md={6}>
              <Box className="tsx-card">

                {/* Live FX badge */}
                <Box className="tsx-card__badge">
                  <span className="tsx-card__ping-wrapper">
                    <span className="tsx-card__ping" />
                    <span className="tsx-card__dot" />
                  </span>
                  <Typography className="tsx-card__badge-text">Live Wholesale FX</Typography>
                </Box>

                <Typography className="tsx-card__title">Treasury Routing Engine</Typography>

                {/* Amount rows */}
                <Box className="tsx-amounts">

                  {/* USD — Staged Allocation */}
                  <Box className="tsx-fx-row">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography className="tsx-fx-row__label">Staged Allocation</Typography>
                      <input
                        type="text"
                        value={usdAmount || ''}
                        placeholder="0.00"
                        onChange={(e) => {
                          setFixedSide('sell');
                          setUsdAmount(formatNumber(e.target.value));
                          setConvertedAmount('');
                        }}
                        onKeyDown={(evt) => {
                          if (
                            !/[\d.\b]/.test(evt.key) &&
                            !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(evt.key)
                          )
                            evt.preventDefault();
                        }}
                        className="tsx-fx-row__input"
                      />
                    </Box>
                    <Box className="tsx-fx-row__currency-box">USD</Box>
                  </Box>

                  {/* Swap arrow */}
                  {/* <Box className="tsx-amounts__arrow-wrap">
                    <Box className="tsx-swap-btn">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </Box>
                  </Box> */}

                  {/* Target currency — Supplier Disbursement */}
                  <Box className="tsx-fx-row">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography className="tsx-fx-row__label">Target Supplier Disbursement</Typography>
                      <input
                        type="text"
                        value={convertedAmount || ''}
                        placeholder="0.00"
                        readOnly
                        className="tsx-fx-row__input tsx-fx-row__input--emerald tsx-fx-row__input--readonly"
                      />
                    </Box>
                    <div className="tsx-fx-select-wrapper" ref={selectWrapperRef}>
                      <button
                        type="button"
                        className="tsx-fx-select-btn"
                        onClick={() => setCurrencyOpen((o) => !o)}
                      >
                        {selectedCurrency}
                        <svg
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          className={currencyOpen ? 'open' : ''}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {currencyOpen && (
                        <ul className="tsx-fx-select-list">
                          {currencies.map(({ code, name }) => (
                            <li
                              key={code}
                              className={`tsx-fx-select-list__item${selectedCurrency === code ? ' tsx-fx-select-list__item--active' : ''}`}
                              onClick={() => {
                                setSelectedCurrency(code);
                                setConvertedAmount('');
                                setCurrencyOpen(false);
                              }}
                            >
                              <span className="tsx-fx-select-list__code">{code}</span>
                              <span className="tsx-fx-select-list__name">{name}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </Box>

                </Box>

                {/* Rate panel */}
                <Box className="tsx-rate-panel">
                  <Box className="tsx-rate-panel__row">
                    <Typography className="tsx-rate-panel__label">Executable Rate:</Typography>
                    <Typography className="tsx-rate-panel__value">
                      {initialRateFetching ? (
                        <Skeleton variant="text" width={160} height={20} />
                      ) : conversionError === '' ? (
                        `1 USD = ${initialRate} ${selectedCurrency}`
                      ) : (
                        <span className="tsx-rate-panel__error">{conversionError}</span>
                      )}
                    </Typography>
                  </Box>
                  <Box className="tsx-rate-panel__row">
                    <Typography className="tsx-rate-panel__label">Settlement Trigger:</Typography>
                    <Typography className="tsx-rate-panel__label" sx={{ color: '#0284c7', fontWeight: 600 }}>
                      Awaiting 19 CFR Clearance
                    </Typography>
                  </Box>
                  <Typography className="tsx-rate-panel__note">
                    Exchange rates are subject to frequent fluctuations.
                  </Typography>
                </Box>

                {/* Convert button */}
                <button
                  type="button"
                  disabled={rateFetching}
                  onClick={handleConversion}
                  className="tsx-convert-btn"
                >
                  {rateFetching ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
                  ) : (
                    'Execute Conversion →'
                  )}
                </button>

              </Box>
            </Grid>

          </Grid>
        </Container>
      </section>

    </Box>
  );
};

export default PlatformArchitecturePage;